import { Slide, TextProps } from '@features/powerpoint';
import { unifiedPlatformColorPalette } from '@utils/unifiedPlatformColorPalette';
import { formatters } from '@utils/formattersV2';
import { contentSlideHeader, textStyles } from '../../../../utils';
import { star } from '../../../../images';
import { NEWLINE_REGEX } from '../../../../domain/key-takeaways';

const NUMBER_VALUE_FONT_SIZE = 30;
const VALUE_SUBTEXT_FONT_SIZE = 10;

const keyTakeawayOptions: TextProps['options'] = {
  color: textStyles.contentSlideDefaults?.color,
  breakLine: true,
  fontSize: 12,
  valign: 'top',
  h: 1.19,
  w: 2.63,
};

const fieldHeaderOptions: TextProps['options'] = {
  color: unifiedPlatformColorPalette.inmarketGreen,
  fontSize: 10,
  bold: true,
  breakLine: true,
};

const fieldValueOptions: TextProps['options'] = {
  ...textStyles.contentSlideDefaults,
  h: 0.2,
  w: 3.26,
  fontSize: 18,
};

const getKeyTakeAwayText = (ftaKeyTakeaways: string): string[] =>
  ftaKeyTakeaways
    .split(NEWLINE_REGEX)
    .map(text => text.replaceAll(/<.*?>/g, '').trim())
    .filter(text => text !== '');

type OverviewSlideProps = {
  slideMaster: string;
  totalVisits: number;
  incrementalVisits: number;
  incrementalLift: number;
  costPerVisit: number;
  costPerIncrementalVisit: number;
  confidenceLevel: number;
  ftaOverallResults: string;
};

export const overallCampaignResultsSlide = (
  props: OverviewSlideProps
): Slide => {
  const keyTakeaways = getKeyTakeAwayText(props.ftaOverallResults);

  const keyTakeawayElements: TextProps[] = keyTakeaways.map(
    (keyTakeaway, index) => ({
      text: keyTakeaway,
      options: {
        ...keyTakeawayOptions,
        x: 0.85 + index * 2.9,
        y: 5.69,
      },
    })
  );

  return {
    slideMaster: props.slideMaster,
    images: [
      {
        path: star,
        x: 0.89,
        y: 5,
        h: 0.51,
        w: 0.51,
      },
    ],
    shapes: [
      {
        shapeName: 'ellipse',
        options: {
          h: 0.6,
          w: 0.6,
          x: 0.85,
          y: 4.96,
          fill: {
            color: 'FFFFFF',
          },
          line: {
            color: unifiedPlatformColorPalette.inmarketGreen,
            width: 1,
          },
        },
      },
      {
        shapeName: 'rect',
        options: {
          h: 0,
          w: 8.87,
          x: 3.88,
          y: 5.28,
          line: {
            color: unifiedPlatformColorPalette.inmarketGreen,
            width: 1,
          },
        },
      },
    ],
    texts: [
      contentSlideHeader('Overall Campaign Results'),
      {
        text: 'Visit Summary',
        options: {
          ...fieldValueOptions,
          x: 0.84,
          y: 1.71,
          h: 0.47,
          w: 4.52,
          valign: 'top',
        },
      },
      {
        text: [
          {
            text: 'Total Visits'.toUpperCase(),
            options: {
              ...fieldHeaderOptions,
            },
          },
          {
            text: formatters.visits(props.totalVisits, { full: true }),
            options: {
              fontSize: NUMBER_VALUE_FONT_SIZE,
              ...textStyles.contentSlideDefaults,
              breakLine: true,
            },
          },
          {
            text: 'Projected Visits After Ad Exposure',
            options: {
              fontSize: VALUE_SUBTEXT_FONT_SIZE,
              ...textStyles.contentSlideDefaults,
            },
          },
        ],
        options: {
          valign: 'top',
          lineSpacingMultiple: 1.2,
          h: 1.19,
          w: 2.63,
          x: 0.85,
          y: 2.3,
        },
      },
      {
        text: [
          {
            text: 'Cost Per Visit'.toUpperCase(),
            options: {
              ...fieldHeaderOptions,
              breakLine: true,
            },
          },
          {
            text: formatters.currency(props.costPerVisit, {
              full: true,
              decimals: 2,
            }),
            options: {
              fontSize: NUMBER_VALUE_FONT_SIZE,
              ...textStyles.contentSlideDefaults,
              breakLine: true,
            },
          },
          {
            text: 'Spend / Total Visits',
            options: {
              fontSize: VALUE_SUBTEXT_FONT_SIZE,
              ...textStyles.contentSlideDefaults,
            },
          },
        ],
        options: {
          lineSpacingMultiple: 1.2,
          valign: 'top',
          h: 1.02,
          w: 2.63,
          x: 0.85,
          y: 3.68,
        },
      },
      ...attributionSummarySection(props),
      {
        text: 'Key Takeaways',
        options: {
          ...fieldValueOptions,
          x: 1.55,
          y: 5.09,
          w: 4.52,
          h: 0.47,
        },
      },
      ...keyTakeawayElements,
    ],
  };
};

const attributionSummarySection = (
  props: Pick<
    OverviewSlideProps,
    | 'incrementalVisits'
    | 'incrementalLift'
    | 'costPerIncrementalVisit'
    | 'confidenceLevel'
  >
): NonNullable<Slide['texts']> => [
  {
    text: 'Attribution Summary',
    options: {
      ...fieldValueOptions,
      x: 6.61,
      y: 1.71,
      h: 0.47,
      w: 4.52,
    },
  },
  {
    text: [
      {
        text: 'Incremental Visits'.toUpperCase(),
        options: {
          ...fieldHeaderOptions,
        },
      },
      {
        text: formatters.visits(props.incrementalVisits, { full: true }),
        options: {
          fontSize: NUMBER_VALUE_FONT_SIZE,
          ...textStyles.contentSlideDefaults,
        },
      },
    ],
    options: {
      lineSpacingMultiple: 1.2,
      valign: 'top',
      h: 1.27,
      w: 2.83,
      x: 6.61,
      y: 2.3,
    },
  },
  {
    text: [
      {
        text: 'Incremental Lift'.toUpperCase(),
        options: {
          ...fieldHeaderOptions,
        },
      },
      {
        text: formatters.lift(props.incrementalLift),
        options: {
          fontSize: NUMBER_VALUE_FONT_SIZE,
          ...textStyles.contentSlideDefaults,
          breakLine: true,
        },
      },
      {
        text: `Confidence Level: ${formatters.confidenceLevel(
          props.confidenceLevel,
          props.incrementalLift
        )}`,
        options: {
          breakLine: true,
          fontSize: VALUE_SUBTEXT_FONT_SIZE,
          ...textStyles.contentSlideDefaults,
        },
      },
    ],
    options: {
      lineSpacingMultiple: 1.2,
      valign: 'top',
      h: 0.76,
      w: 3.13,
      x: 9.62,
      y: 2.3,
    },
  },
  {
    text: [
      {
        text: 'Cost Per Incremental Visit'.toUpperCase(),
        options: {
          ...fieldHeaderOptions,
        },
      },
      {
        text: formatters.currency(props.costPerIncrementalVisit, {
          decimals: 2,
        }),
        options: {
          fontSize: NUMBER_VALUE_FONT_SIZE,
          ...textStyles.contentSlideDefaults,
          breakLine: true,
        },
      },
      {
        text: 'Spend / Incremental Visits',
        options: {
          breakLine: true,
          fontSize: VALUE_SUBTEXT_FONT_SIZE,
          ...textStyles.contentSlideDefaults,
        },
      },
    ],
    options: {
      lineSpacingMultiple: 1.2,
      valign: 'top',
      h: 0.76,
      w: 2.63,
      x: 6.61,
      y: 3.68,
    },
  },
];
